import React, {
    createContext, useState, useContext, useEffect, useReducer
} from 'react';
import {
    RootReducer
} from './reducers/RootReducer';
import {
    PromptLabData, AiTeacherData, CodeSandboxData
} from './initialData';
import {
    useParams
} from 'react-router-dom';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    opcodeAssistantService
} from 'shared/api';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    modelService
} from 'shared/api/services/BEX/models.services';

// Create the parent context
export const AiLabContext = createContext({});
// Parent provider
export const AiLabProvider = ({
    children
}) => {
    // use Reducers
    const [state, dispatch] = useReducer(RootReducer, {
        promptLab: PromptLabData,
        aiTeacher: AiTeacherData,
        codeSandbox: CodeSandboxData
    });

    const {
        oragID, subOragID
    } = useParams();
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const [isLoadingInstructions, setIsLoadingInstructions] = useState(false);
    const [modelProviders, setModelProviders] = useState([]);

    const handleReloadModels = async (hardRefresh = false) => {
        const body = {
            provider: selectedOrganization.providers || [],
            filter: {
                Watson: {
                    region: 'All',
                    showInactive: false
                },
                Groq: {
                    showInactive: false
                },
                Cerebras: {
                    showInactive: false
                },
                Gemini: {
                    showInactive: false
                },
                Mistral: {
                    showInactive: false
                },
                Novita: {
                    showInactive: false
                }
            }
        };
        const res = await modelService.getModels(body, hardRefresh, oragID);
        const models = res.data || [];
        models.sort((a, b) => {
            let providerComparison = a.provider_type.localeCompare(b.provider_type);
            return providerComparison !== 0 ? providerComparison : a.model_id.localeCompare(b.model_id);
        });
        setModelProviders(models);

        return models;
    };

    const fetchInstructions = async () => {
        try {
            setIsLoadingInstructions(true);

            const models = await handleReloadModels();

            const modelIdSet = new Set(models.map(model => model.model_id));

            const res = await instructionService.listModels(
                oragID,
                subOragID,
            );

            const instructions = res.data || [];
            const fetchedInstructions = instructions.map((instruction) => {
                const modifiedByMember = selectedOrganization.members.find(member => member.id._id === instruction.modified_by);
                const userIdMember = selectedOrganization.members.find(member => member.id._id === instruction.createdBy);

                const parameters = instruction.parameters || {};
                // update the parameters for IBM model from sampling to sample
                if (instruction.model_type === 'IBM' && instruction.parameters?.decoding_method === 'sampling') {
                    parameters['decoding_method'] = 'sample';
                }

                return {
                    ...instruction,
                    parameters,
                    modified_by: modifiedByMember ? modifiedByMember.id.name : instruction.modified_by + ' (removed user)',
                    user_id: userIdMember ? userIdMember.id.name : instruction.createdBy + ' (removed user)',
                    isModelProviderExist: modelIdSet.has(instruction.modelId)
                };
            });
            dispatch({
                type: 'SET_INSTRUCTIONS',
                field: 'savedInstructions',
                value: fetchedInstructions
            });
        } catch (error) {
            console.error('Error fetching instructions:', error);
        } finally {
            setIsLoadingInstructions(false);
        }
    };

    const fetchAiTeacherAssistant = async () => {
        try {

            const assistant = await opcodeAssistantService.getOpcodeAssistant(
                state?.aiTeacher?.COMMON_ORG,
                state?.aiTeacher?.COMMON_SUBORG,
                state?.aiTeacher?.AI_TEACHER_ASST_ID
            );

            const opcodesList = assistant?.data?.opcodes || [];
            const opcodesMap = {
                teacher: {
                    ...state?.aiTeacher?.aiTeacherOpcodes?.teacher,
                    opcode: opcodesList.find((opcode) => opcode.includes('TEACHER_MODE')) || null
                },
                quiz: {
                    ...state?.aiTeacher?.aiTeacherOpcodes?.quiz,
                    opcode: opcodesList.find((opcode) => opcode.includes('QUIZ_MODE')) || null
                }
            };

            dispatch({
                type: 'AI_TEACHER_SET_OPCODES',
                value: opcodesMap
            });
        } catch (error) {
            console.error('Error fetching assistants:', error);
        }
    };

    useEffect(() => {
    // Populate instructions on load of ai lab
        fetchInstructions();

        // Retrieve Assistant Details in AI Teacher
        fetchAiTeacherAssistant();

    }, []);

    // useEffect(() => {
    //   //
    //   //
    // }, [state.promptLab, state.aiTeacher])

    // context vars
    const values = {
    // reducers
        state,
        dispatch,

        // ===== Live Prompt =====
        instructions: state.promptLab.savedInstructions,
        fetchInstructions,
        isLoadingInstructions,
        // model providers
        modelProviders,
        handleReloadModels,

        // ===== AI Teacher =====
        aiTeacherMessages: state.aiTeacher.aiTeacherMessages,

        // ===== Code Sandbox =====
        codeSandboxMessages: state.codeSandbox.codeSandboxMessages
    };

    return (
        <AiLabContext.Provider value={values}>
            {children}
        </AiLabContext.Provider>
    );
};